/**
 * The what-if lifecycle behind Proposals' edit-and-compare flow: a temporary
 * portfolio is built from an edited holdings list, run through the same
 * compute pipeline as a real one, read back through `getPortfolio`, and torn
 * down again once the comparison is no longer needed.
 */
import { gql } from '@apollo/client';

/**
 * Runs the named compute stages for one portfolio and persists the results —
 * the same pipeline a real portfolio is kept current with. Every flag
 * defaults false; the caller asks for exactly the stages it needs.
 */
export const RECOMPUTE_PORTFOLIO_FULL = gql`
  mutation RecomputePortfolioFull(
    $securitySymbol: String
    $userId: Int
    $asOf: Date
    $benchmarkSymbol: String
    $reprice: Boolean = false
    $weights: Boolean = false
    $unrealizedPnl: Boolean = false
    $returns: Boolean = false
    $valuations: Boolean = false
    $fundamentals: Boolean = false
    $realizedRisk: Boolean = false
    $realizedStressRisk: Boolean = false
    $factorExposures: Boolean = false
    $classification: Boolean = false
    $returnAttribution: Boolean = false
    $brinson: Boolean = false
    $multifactorAttribution: Boolean = false
    $holdingReturnContributions: Boolean = false
    $marketDataHistory: Boolean = false
  ) {
    recomputePortfolioFull(
      securitySymbol: $securitySymbol
      userId: $userId
      asOf: $asOf
      benchmarkSymbol: $benchmarkSymbol
      reprice: $reprice
      weights: $weights
      unrealizedPnl: $unrealizedPnl
      returns: $returns
      valuations: $valuations
      fundamentals: $fundamentals
      realizedRisk: $realizedRisk
      realizedStressRisk: $realizedStressRisk
      factorExposures: $factorExposures
      classification: $classification
      returnAttribution: $returnAttribution
      brinson: $brinson
      multifactorAttribution: $multifactorAttribution
      holdingReturnContributions: $holdingReturnContributions
      marketDataHistory: $marketDataHistory
    )
  }
`;

/** Deletes one or more portfolios outright — used to clear a what-if the moment it stops being needed. */
export const DELETE_PORTFOLIOS = gql`
  mutation DeletePortfolios($securitySymbols: [String!]!) {
    deletePortfolioComplete(securitySymbols: $securitySymbols) {
      success
      message
      portfoliosDeleted
      rowsDeleted
      deletedSymbols
      blockedSymbols
      blockedBy
    }
  }
`;

/** Everything the backtest reports for one composition, on both sides of a change. */
const BACKTEST_METRICS = `
  cagr annReturn annVolatility sharpe sortino
  maxDrawdown currentDrawdown var95 var99 cvar95 cvar99
  beta trackingError returnSinceInception return1y
`;

/**
 * A composition, measured against the one it would replace.
 *
 * `portfolioSymbol` is priced as it stands and again with `trades` applied, over
 * `lookbackDays` of history, and both readings come back together with their
 * difference already taken. It is the scenario engine's own backtest, so it
 * needs nothing computed in advance: a portfolio created moments earlier can be
 * sent straight here.
 *
 * `currentTotalValue` and `newTotalValue` are selected but answer 0 on every
 * portfolio sampled, so nothing should render money from this — the metrics are
 * rates and ratios, which is what a composition can honestly be judged on.
 *
 * The trades carry no price: the backend values each at its own last close or
 * NAV, and passing one made no difference to any figure returned.
 *
 * WHAT COMES BACK IS ALL THERE IS, and both backtest panels render exactly it:
 * three `BacktestMetricsType` (before, after, and the difference) carrying
 * fifteen rates and ratios each, the factor maps with their coverage, and the
 * four exposure lists with the concentration change. There are no fundamentals
 * here, no return ladder beyond `return1y` and `returnSinceInception`, and no
 * per-holding rows — a screen wanting those is reading a portfolio record, not
 * this.
 */
export const BACKTEST_PORTFOLIO_TRADES = gql`
  mutation BacktestPortfolioTrades(
    $portfolioSymbol: String!
    $trades: [BacktestTradeInput!]!
    $lookbackDays: Int
    $benchmarkSymbol: String
  ) {
    backtestPortfolioTrades(
      portfolioSymbol: $portfolioSymbol
      trades: $trades
      lookbackDays: $lookbackDays
      benchmarkSymbol: $benchmarkSymbol
      includeScenarios: false
    ) {
      portfolioSymbol
      calculationTimeMs
      currentMetrics { ${BACKTEST_METRICS} }
      proformaMetrics { ${BACKTEST_METRICS} }
      deltaMetrics { ${BACKTEST_METRICS} }
      factorExposures { current proforma delta currentCoverage proformaCoverage }
      exposureChanges {
        assetClassChanges { name currentWeight newWeight change }
        sectorChanges { name currentWeight newWeight change }
        industryChanges { name currentWeight newWeight change }
        countryChanges { name currentWeight newWeight change }
        concentrationChange
      }
    }
  }
`;
